import { embedExifIntoJpegBlob, formatExifDateTime } from './exif-embed';
import { FileIntakeResult, FileMetadata } from './kyc-file-metadata.types';

const APP_CAMERA_LABEL = 'SomosKycV1 in-app camera (live)';

export interface CameraCaptureInfo {
  width: number;
  height: number;
  facingMode?: string;
  deviceLabel?: string;
  frameRate?: number;
  capturedAt: Date;
}

/** Derive a coarse device/software string from the UA (best-effort, not sensor EXIF). */
function uaSoftware(): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  return ua ? `${APP_CAMERA_LABEL} - ${ua}` : APP_CAMERA_LABEL;
}

/** Build the synthetic, explicitly-app-labelled metadata for a live capture. No GPS. */
export function buildCameraMetadata(info: CameraCaptureInfo): FileMetadata {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
  return {
    file: {
      originalName: 'kyc-camera-capture.jpg',
      mimeType: 'image/jpeg',
      sizeBytes: 0, // filled by caller if needed; storageBlob.size is authoritative
      lastModified: info.capturedAt.getTime(),
    },
    source: 'camera',
    synthetic: true,
    exif: {
      Software: APP_CAMERA_LABEL,
      ImageDescription: APP_CAMERA_LABEL,
      DateTimeOriginal: formatExifDateTime(info.capturedAt),
      PixelXDimension: info.width,
      PixelYDimension: info.height,
    },
    camera: {
      capturedVia: 'app-camera-live',
      facingMode: info.facingMode,
      deviceLabel: info.deviceLabel,
      width: info.width,
      height: info.height,
      frameRate: info.frameRate,
      userAgent: ua,
    },
  };
}

/**
 * Attach the synthetic EXIF block to a camera capture JPEG and return it as the
 * raw storage blob. Fail-open: if embedding fails, returns the original JPEG with
 * the metadata still populated (DB column keeps the record).
 */
export async function intakeCameraCapture(
  jpeg: Blob,
  info: CameraCaptureInfo,
  name: string,
): Promise<FileIntakeResult> {
  const metadata = buildCameraMetadata(info);
  metadata.file.originalName = name;
  metadata.file.sizeBytes = jpeg.size;
  let storageBlob = jpeg;
  try {
    storageBlob = await embedExifIntoJpegBlob(jpeg, {
      software: uaSoftware(),
      imageDescription: APP_CAMERA_LABEL,
      dateTimeOriginal: formatExifDateTime(info.capturedAt),
      pixelXDimension: info.width,
      pixelYDimension: info.height,
    });
  } catch {
    /* keep original jpeg — metadata still recorded in the DB column */
  }
  return { displayBlob: jpeg, storageBlob, storageName: name, metadata };
}
